import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

import OceanBackground from './components/svg/OceanBackground';
import ScrollytellingIntro from './components/ScrollytellingIntro';
import UnlockGame from './components/UnlockGame';
import SwipeSteerGame from './components/SwipeSteerGame';
import WizardSteps from './components/WizardSteps';
import FinalReveal from './components/FinalReveal';

// App states
const SCREENS = {
  INTRO: 'intro',
  GAME1: 'game1',      // Catch the boat
  GAME2: 'game2',      // Swipe steer
  WIZARD: 'wizard',
  FINAL: 'final'
};

// LocalStorage key
const STORAGE_KEY = 'boat-voucher-state';

// Default selections
const defaultSelections = {
  boatType: null,
  location: null,
  date: null,
  time: null,
  people: 2,
  addon: 'none',
  customWish: ''
};

function App() {
  const [currentScreen, setCurrentScreen] = useState(SCREENS.INTRO);
  const [selections, setSelections] = useState(defaultSelections);

  // Load saved state from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.selections) {
          setSelections(parsed.selections);
        }
        // Don't restore screen - always start from intro for the experience
      }
    } catch (e) {
      console.log('No saved state found');
    }
  }, []);

  // Save state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        selections,
        lastUpdated: new Date().toISOString()
      }));
    } catch (e) {
      console.log('Could not save state');
    }
  }, [selections]);

  // Screen transitions
  const handleIntroComplete = () => {
    setCurrentScreen(SCREENS.GAME1);
  };

  const handleGame1Complete = () => {
    setCurrentScreen(SCREENS.GAME2);
  };

  const handleGame2Complete = () => {
    setCurrentScreen(SCREENS.WIZARD);
  };

  const handleWizardComplete = () => {
    setCurrentScreen(SCREENS.FINAL);
  };

  const handleRestart = () => {
    setSelections(defaultSelections);
    setCurrentScreen(SCREENS.INTRO);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <div className="app-container min-h-screen" data-testid="app-container">
      {/* Ocean background - always visible */}
      <OceanBackground />

      {/* Screen content */}
      <AnimatePresence mode="wait">
        {currentScreen === SCREENS.INTRO && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <ScrollytellingIntro onComplete={handleIntroComplete} />
          </motion.div>
        )}

        {currentScreen === SCREENS.GAME1 && (
          <motion.div
            key="game1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <UnlockGame onComplete={handleGame1Complete} />
          </motion.div>
        )}

        {currentScreen === SCREENS.GAME2 && (
          <motion.div
            key="game2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <SwipeSteerGame onComplete={handleGame2Complete} />
          </motion.div>
        )}

        {currentScreen === SCREENS.WIZARD && (
          <motion.div
            key="wizard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <WizardSteps 
              selections={selections}
              setSelections={setSelections}
              onComplete={handleWizardComplete}
            />
          </motion.div>
        )}

        {currentScreen === SCREENS.FINAL && (
          <motion.div
            key="final"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <FinalReveal 
              selections={selections}
              onRestart={handleRestart}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
